import { useMemo } from "react"
import { ChevronRight, ChevronDown, X } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { normalizeStr } from "@/app/lib/str"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { ACQ_GREEN, DIV_RED_SOFT } from "@/app/lib/txColors"
import { deriveEffectivePosition, FULL_ROYALTY_DISPLAY } from "@/app/lib/batchTransactions"
import { interestStatusOptions, showExpiration, showOgl } from "@/app/lib/interestKindRules"
import { StrCombobox } from "@/app/components/StrCombobox"
import { SelectMenu } from "@/app/components/SelectMenu"
import type { RowState, Mode, InterestKind, InterestType } from "./types"
import type { StrOptions } from "./useStrOptions"

// Number of <td>s in the main row, per interest kind. The parent header <tr>
// must match, and the expanded detail row spans all of them.
// leasehold: toggle,Type,Date,STR,County,NetAcres,NRI%,ORRI(640),NAOwned,NRIOwned,ORRI%,remove
// mineral:   toggle,Type,Date,STR,County,NetAcres,Royalty,NAOwned,remove
export function entryRowColSpan(kind: InterestKind): number {
  return kind === "leasehold" ? 12 : 9
}

interface TransactionEntryRowProps {
  row: RowState
  rows: RowState[] // full grid, for effective-position derivation
  kind: InterestKind
  strOptions: StrOptions
  errors?: string[]
  onPatch: (patch: Partial<RowState>) => void
  onSelectStr: (str: string) => void // parent sets str + autofills county
  onRemove: () => void
  theme: ClientTheme
}

/**
 * One editable row of the multi-transaction entry grid, plus an expandable
 * detail panel. Pure presentational / derive-on-render — no effects. All derived
 * figures come from `deriveEffectivePosition` computed during render; the parent
 * owns the `RowState[]` and passes callbacks.
 */
export function TransactionEntryRow({
  row,
  rows,
  kind,
  strOptions,
  errors,
  onPatch,
  onSelectStr,
  onRemove,
  theme,
}: TransactionEntryRowProps) {
  const isDiv = row.mode === "DIV"
  const hasError = !!errors?.length

  // DIV rows may divest owned STRs OR any STR acquired by an ACQ row elsewhere
  // in this same batch (an in-batch acquisition is immediately divestible).
  const divOptions = useMemo(() => {
    const set = new Set(strOptions.ownedStrsFor(kind))
    for (const r of rows) {
      if (r.mode !== "ACQ") continue
      const canonical = normalizeStr(r.str)
      if (canonical) set.add(canonical)
    }
    return [...set].sort()
  }, [strOptions.ownedStrsFor, kind, rows])

  // Render-time derived position — no effect. Pre-batch inventory for the STR
  // plus every other in-grid row targeting the same STR.
  const base = strOptions.inventoryByStr.get(normalizeStr(row.str) ?? "") ?? null
  const pos = deriveEffectivePosition(base, rows, row.id, kind)

  // Mineral DIV Legal Desc. options: distinct legal descriptions from kept
  // mineral ACQs for this STR (server-provided) plus in-grid ACQ rows for the
  // same STR. Only consulted on the mineral tab, where every grid row is mineral.
  const legalDescOptions = useMemo(() => {
    const set = new Set((base?.kinds?.mineral.legal_descs ?? []).map((d) => d.trim()))
    const target = normalizeStr(row.str)
    if (target) {
      for (const r of rows) {
        if (r.mode !== "ACQ" || normalizeStr(r.str) !== target) continue
        const desc = r.legal_desc.trim()
        if (desc) set.add(desc)
      }
    }
    set.delete("")
    return [...set].sort()
  }, [base, rows, row.str])

  const cellTd: React.CSSProperties = {
    padding: "4px 6px",
    verticalAlign: "middle",
    borderBottom: hasError ? `1px solid ${theme.brandAccent}` : "none",
  }
  // Subtle left affordance on the row when it has validation errors.
  const firstCellTd: React.CSSProperties = {
    ...cellTd,
    borderLeft: hasError ? `3px solid ${theme.brandAccent}` : "3px solid transparent",
  }

  const orriDisplay = row.override_orri
    ? row.manual_orri_percent
    : (pos.orriReserved * 100).toFixed(4)

  return (
    <>
      <tr>
        {/* 1 — Expand toggle */}
        <td style={firstCellTd}>
          <button
            type="button"
            aria-label={row.expanded ? "Collapse row" : "Expand row"}
            onClick={() => onPatch({ expanded: !row.expanded })}
            style={iconButtonStyle(theme)}
          >
            {row.expanded ? (
              <ChevronDown size={15} style={{ color: theme.textMuted }} />
            ) : (
              <ChevronRight size={15} style={{ color: theme.textMuted }} />
            )}
          </button>
        </td>

        {/* 2 — Type (ACQ / DIV) */}
        <td style={cellTd}>
          <div className="flex gap-1">
            {(["ACQ", "DIV"] as Mode[]).map((m) => {
              const active = row.mode === m
              const activeBg = m === "ACQ" ? ACQ_GREEN : DIV_RED_SOFT
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => onPatch({ mode: m })}
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "11px",
                    fontWeight: active ? 600 : 500,
                    padding: "5px 9px",
                    borderRadius: "6px",
                    backgroundColor: active ? activeBg : theme.bgSurface2,
                    color: active ? "#FFFFFF" : theme.textMuted,
                    border: `1px solid ${active ? activeBg : theme.borderColor}`,
                    cursor: "pointer",
                  }}
                >
                  {m}
                </button>
              )
            })}
          </div>
        </td>

        {/* 3 — Date */}
        <td style={cellTd}>
          <input
            type="date"
            value={row.transaction_date}
            onChange={(e) => onPatch({ transaction_date: e.target.value })}
            style={inputStyle(theme)}
          />
        </td>

        {/* 4 — STR */}
        <td style={{ ...cellTd, minWidth: 150 }}>
          <StrCombobox
            value={row.str}
            onSelect={onSelectStr}
            allowCreate={row.mode === "ACQ"}
            options={isDiv ? divOptions : strOptions.statewideStrs}
            theme={theme}
            invalid={hasError}
          />
        </td>

        {/* 5 — County */}
        <td style={cellTd}>
          <input
            value={row.county}
            onChange={(e) => onPatch({ county: e.target.value })}
            placeholder="Auto"
            style={inputStyle(theme)}
          />
        </td>

        {/* 6 — Net Acres (locked to net position when DIV + ARTI) */}
        <td style={cellTd}>
          {isDiv && row.arti ? (
            <ReadonlyCell value={pos.naOwned.toFixed(4)} theme={theme} />
          ) : (
            <input
              type="number"
              step="any"
              value={row.net_acres}
              onChange={(e) => onPatch({ net_acres: e.target.value })}
              style={inputStyle(theme)}
            />
          )}
        </td>

        {/* 7 — Leasehold: NRI % (bound to the field matching the mode).
               Mineral: Royalty (ACQ only — Open locks it at the full royalty). */}
        <td style={cellTd}>
          {kind === "mineral" ? (
            isDiv ? (
              <BlankCell theme={theme} />
            ) : row.interest_type === "Open" ? (
              <ReadonlyCell value={FULL_ROYALTY_DISPLAY} theme={theme} />
            ) : (
              <input
                type="number"
                step="any"
                placeholder="0.1875"
                value={row.royalty}
                onChange={(e) => onPatch({ royalty: e.target.value })}
                style={inputStyle(theme)}
              />
            )
          ) : (
            <input
              type="number"
              step="any"
              placeholder="81.25"
              value={isDiv ? row.nri_assigned_percent : row.nri_percent}
              onChange={(e) =>
                onPatch(
                  isDiv ? { nri_assigned_percent: e.target.value } : { nri_percent: e.target.value }
                )
              }
              style={inputStyle(theme)}
            />
          )}
        </td>

        {/* 8 — ORRI (640) (leasehold ACQ only) */}
        {kind === "leasehold" && (
          <td style={cellTd}>
            {isDiv ? (
              <BlankCell theme={theme} />
            ) : (
              <input
                type="number"
                step="any"
                value={row.orri_640}
                onChange={(e) => onPatch({ orri_640: e.target.value })}
                style={inputStyle(theme)}
              />
            )}
          </td>
        )}

        {/* 9 — NA Owned (DIV only) */}
        <td style={cellTd}>
          {isDiv ? (
            <ReadonlyCell value={formatNumber(pos.naOwned, 4)} theme={theme} />
          ) : (
            <BlankCell theme={theme} />
          )}
        </td>

        {/* 10 — NRI Owned (leasehold DIV only) */}
        {kind === "leasehold" && (
          <td style={cellTd}>
            {isDiv ? (
              <ReadonlyCell value={decimalToPercent(pos.nriOwned, 4)} theme={theme} />
            ) : (
              <BlankCell theme={theme} />
            )}
          </td>
        )}

        {/* 11 — ORRI % (leasehold DIV only; display — override is edited in the detail panel) */}
        {kind === "leasehold" && (
          <td style={cellTd}>
            {isDiv ? (
              <ReadonlyCell value={orriDisplay} theme={theme} />
            ) : (
              <BlankCell theme={theme} />
            )}
          </td>
        )}

        {/* 12 — Remove */}
        <td style={cellTd}>
          <button
            type="button"
            aria-label="Remove row"
            onClick={onRemove}
            style={iconButtonStyle(theme)}
          >
            <X size={15} style={{ color: theme.textDim }} />
          </button>
        </td>
      </tr>

      {hasError && !row.expanded && (
        <tr>
          <td colSpan={entryRowColSpan(kind)} style={{ padding: "2px 6px 8px 12px" }}>
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.brandAccent,
              }}
            >
              {errors!.join(", ")}
            </span>
          </td>
        </tr>
      )}

      {row.expanded && (
        <tr>
          <td colSpan={entryRowColSpan(kind)} style={{ padding: "4px 12px 16px 12px" }}>
            <div
              className="rounded-xl p-4"
              style={{
                backgroundColor: theme.bgSurface2,
                border: `1px solid ${theme.borderColor}`,
              }}
            >
              {hasError && (
                <div
                  className="mb-3"
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "12px",
                    color: theme.brandAccent,
                  }}
                >
                  {errors!.join(", ")}
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {isDiv ? (
                  // DIV status is derived server-side from the latest ACQ for the
                  // STR — show the predicted value, never an editable control.
                  <DetailField label="Interest Status (auto)" theme={theme} width="140px">
                    <ReadonlyCell value={pos.divStatus ?? "—"} theme={theme} />
                  </DetailField>
                ) : (
                  <DetailField label="Interest Status" theme={theme} width="140px">
                    <SelectMenu
                      value={row.interest_type}
                      onChange={(v) => onPatch({ interest_type: v as InterestType })}
                      options={interestStatusOptions(kind)}
                      placeholder="—"
                      width="140px"
                      background={theme.bgSurface2}
                      theme={theme}
                    />
                  </DetailField>
                )}

                {showExpiration(row.mode, row.interest_type) && (
                  <DetailField label="Expiration" theme={theme} width="160px">
                    <input
                      type="date"
                      value={row.expiration_date}
                      onChange={(e) => onPatch({ expiration_date: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>
                )}

                <DetailField label="Counterparty" theme={theme}>
                  <input
                    value={row.counterparty}
                    onChange={(e) => onPatch({ counterparty: e.target.value })}
                    style={inputStyle(theme)}
                  />
                </DetailField>

                {kind === "leasehold" ? (
                  <DetailField label="Depths" theme={theme}>
                    <input
                      value={row.depths}
                      onChange={(e) => onPatch({ depths: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>
                ) : (
                  <DetailField label="Legal Desc." theme={theme}>
                    {isDiv ? (
                      <SelectMenu
                        value={row.legal_desc}
                        onChange={(v) => onPatch({ legal_desc: v })}
                        options={legalDescOptions}
                        placeholder="—"
                        width="200px"
                        background={theme.bgSurface2}
                        theme={theme}
                      />
                    ) : (
                      <input
                        value={row.legal_desc}
                        onChange={(e) => onPatch({ legal_desc: e.target.value })}
                        style={inputStyle(theme)}
                      />
                    )}
                  </DetailField>
                )}

                <DetailField label="Price" theme={theme} width="140px">
                  <input
                    type="number"
                    step="any"
                    value={row.price}
                    onChange={(e) => onPatch({ price: e.target.value })}
                    style={inputStyle(theme)}
                  />
                </DetailField>

                <DetailField label="Bk/Pg" theme={theme} width="140px">
                  <input
                    value={row.book_page}
                    onChange={(e) => onPatch({ book_page: e.target.value })}
                    style={inputStyle(theme)}
                  />
                </DetailField>
              </div>

              {/* Optional lease (OGL) detail for leased mineral acquisitions. The
                  OGL Bk/Pg is the lease's recording, distinct from the
                  transaction's own Bk/Pg above. */}
              {showOgl(kind, row.mode, row.interest_type) && (
                <div className="flex flex-wrap gap-4 mt-4">
                  <DetailField label="OGL Lessor" theme={theme}>
                    <input
                      value={row.ogl_lessor}
                      onChange={(e) => onPatch({ ogl_lessor: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>

                  <DetailField label="OGL Lessee" theme={theme}>
                    <input
                      value={row.ogl_lessee}
                      onChange={(e) => onPatch({ ogl_lessee: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>

                  <DetailField label="OGL Date" theme={theme} width="160px">
                    <input
                      type="date"
                      value={row.ogl_date}
                      onChange={(e) => onPatch({ ogl_date: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>

                  <DetailField label="OGL Term" theme={theme} width="140px">
                    <input
                      value={row.ogl_term}
                      onChange={(e) => onPatch({ ogl_term: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>

                  <DetailField label="OGL Bk/Pg" theme={theme} width="140px">
                    <input
                      value={row.ogl_book_page}
                      onChange={(e) => onPatch({ ogl_book_page: e.target.value })}
                      style={inputStyle(theme)}
                    />
                  </DetailField>
                </div>
              )}

              {isDiv && (
                <div className="flex flex-wrap items-end gap-4 mt-4">
                  <label
                    className="flex items-center gap-2"
                    style={{ cursor: "pointer" }}
                    title="All Rights, Title & Interest — locks Net Acres to the current net position for this STR"
                  >
                    <input
                      type="checkbox"
                      checked={row.arti}
                      onChange={(e) => onPatch({ arti: e.target.checked })}
                    />
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "12px",
                        color: theme.textPrimary,
                      }}
                    >
                      ARTI (lock Net Acres to net position)
                    </span>
                  </label>

                  {kind === "leasehold" && (
                    <>
                      <label className="flex items-center gap-2" style={{ cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={row.override_orri}
                          onChange={(e) =>
                            e.target.checked
                              ? onPatch({
                                  override_orri: true,
                                  manual_orri_percent:
                                    row.manual_orri_percent || (pos.orriReserved * 100).toFixed(4),
                                })
                              : onPatch({ override_orri: false })
                          }
                        />
                        <span
                          style={{
                            fontFamily: "var(--font-ui)",
                            fontSize: "12px",
                            color: theme.textMuted,
                          }}
                        >
                          Override ORRI
                        </span>
                      </label>

                      <DetailField label="ORRI Reserved (%)" theme={theme} width="160px">
                        <input
                          type="number"
                          step="any"
                          value={
                            row.override_orri
                              ? row.manual_orri_percent
                              : (pos.orriReserved * 100).toFixed(4)
                          }
                          onChange={(e) => onPatch({ manual_orri_percent: e.target.value })}
                          disabled={!row.override_orri}
                          style={{
                            ...inputStyle(theme),
                            ...(row.override_orri ? {} : { opacity: 0.6, cursor: "not-allowed" }),
                          }}
                        />
                      </DetailField>
                    </>
                  )}
                </div>
              )}

              <div className="mt-4">
                <DetailField label="Notes" theme={theme} width="100%">
                  <textarea
                    value={row.notes}
                    onChange={(e) => onPatch({ notes: e.target.value })}
                    rows={3}
                    style={inputStyle(theme)}
                  />
                </DetailField>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ── Small presentational helpers (replicated from NewTransactionPage; that page
// is being rewritten separately, so we keep this row self-contained) ──────────

function inputStyle(theme: ClientTheme): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 11px",
    borderRadius: "8px",
    backgroundColor: theme.bgSurface2,
    border: `1px solid ${theme.borderColor}`,
    color: theme.textPrimary,
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    outline: "none",
  }
}

function iconButtonStyle(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    borderRadius: "6px",
    background: "none",
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}

// A non-editable figure pulled from the live/derived position (greyed).
function ReadonlyCell({ value, theme }: { value: string; theme: ClientTheme }) {
  return (
    <div
      style={{
        ...inputStyle(theme),
        backgroundColor: theme.bgPage,
        color: theme.textMuted,
        fontFamily: "var(--font-data)",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  )
}

// Greyed em-dash placeholder for columns that only apply to DIV rows.
function BlankCell({ theme }: { theme: ClientTheme }) {
  return (
    <div
      style={{
        textAlign: "center",
        color: theme.textDim,
        fontFamily: "var(--font-data)",
        fontSize: "13px",
      }}
    >
      —
    </div>
  )
}

function DetailField({
  label,
  theme,
  width = "200px",
  children,
}: {
  label: string
  theme: ClientTheme
  width?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5" style={{ width }}>
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "11px",
          fontWeight: 600,
          color: theme.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}
